import { createApi } from '@reduxjs/toolkit/query/react';
import { DateFormats, formatDate } from '@strata/helper-functions';

import { SCOPE_ASSESSOR_API_URL } from 'api/APIBase';
import { handleError, handleRTKQueryError } from 'services/Error';
import { getBaseQuery } from 'store/api/apiBaseQuery';

export interface BenefitCheckPayload {
    patient_first_name?: string;
    patient_last_name?: string;
    patient_address_line_1?: string;
    patient_address_line_2?: string;
    member_city?: string;
    member_state?: string;
    member_zip?: string;
    gender?: string;
    member_phone?: string;
    account?: string;
    coverage_start_date?: string;
    coverage_end_date?: string;
    is_eligible: boolean;
    attributes?: { attribute_type: string; attribute_values: string[] }[];
}

export interface MemberDetails {
    memberId: string;
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    gender: string;
    phone?: string;
    email?: string;
    memberAddress1?: string;
    memberAddress2?: string;
    memberCity?: string;
    memberState?: string;
    memberZip?: string;
}

export const scopeAssessorApi = createApi({
    baseQuery: getBaseQuery(SCOPE_ASSESSOR_API_URL),
    reducerPath: 'scopeAssessorApi',
    endpoints: (build) => ({
        getBenefitCheck: build.query<
            BenefitCheckPayload | undefined,
            {
                payerId: string;
                segmentId: string;
                memberId: string;
                memberDateOfBirth: string | Date;
                memberFirstName: string;
                memberLastName: string;
            }
        >({
            query: ({
                payerId,
                segmentId,
                memberId,
                memberDateOfBirth,
                memberFirstName,
                memberLastName,
            }) => ({
                url: `${payerId}/benefits/benefit-check`,
                params: {
                    segment_id: segmentId,
                    cardholder_id: memberId,
                    date_of_birth: formatDate(memberDateOfBirth, DateFormats.HYPHEN_DATE),
                    first_name: memberFirstName,
                    last_name: memberLastName,
                },
            }),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred checking member benefits.',
            ),
        }),
        getMemberDetails: build.query<
            MemberDetails | undefined,
            {
                payerId: string;
                segmentId: string;
                memberId: string;
                firstName: string;
                lastName: string;
                dateOfBirth: string;
            }
        >({
            query: ({ payerId, segmentId, memberId, firstName, lastName, dateOfBirth }) => ({
                url: `${payerId}/members/${memberId}`,
                params: {
                    segment_id: segmentId,
                    firstname: firstName,
                    lastname: lastName,
                    dateofbirth: dateOfBirth,
                    ignore_dates: true,
                },
            }),
            transformResponse: (
                response: {
                    member_id: string;
                    firstname: string;
                    lastname: string;
                    dateofbirth: string;
                    gender: string;
                    phone?: string;
                    email?: string;
                    member_address1?: string;
                    member_address2?: string;
                    member_city?: string;
                    member_state?: string;
                    member_zip?: string;
                } | null,
            ) =>
                response
                    ? {
                          memberId: response.member_id,
                          firstName: response.firstname,
                          lastName: response.lastname,
                          dateOfBirth: response.dateofbirth,
                          gender: response.gender,
                          phone: response.phone,
                          email: response.email,
                          memberAddress1: response.member_address1,
                          memberAddress2: response.member_address2,
                          memberCity: response.member_city,
                          memberState: response.member_state,
                          memberZip: response.member_zip,
                      }
                    : undefined,
            transformErrorResponse: (err) => {
                if (err.status === 404) {
                    handleError('Member not found.', err);
                } else {
                    handleError('An error occurred retrieving member details.', err);
                }
            },
        }),
    }),
});

export const { useGetBenefitCheckQuery, useLazyGetBenefitCheckQuery, useGetMemberDetailsQuery } =
    scopeAssessorApi;
